/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';

import { TApiError } from '@global-types/api/api';
import {
	TApiResponseCreateProducer,
	TBodyCreateUser,
} from '@features/resources/producers/types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addUser = (
	body: TBodyCreateUser
): Promise<TApiResponseCreateProducer> => {
	return apiClient.post('/users', body);
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseAddUserOptions = {
	mutationConfig?: TMutationConfig<typeof addUser>;
	page: number;
	size: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddUser = ({
	mutationConfig,
	page,
	size,
}: TUseAddUserOptions) => {
	const { t } = useTranslation();

	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addUser,
		onSuccess(...args) {
			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			console.log('error ==== ', error.message);
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`${error.message}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
